import { supabase } from '../supabaseClient'

/**
 * Lógica compartilhada de anexos de documentos do cliente.
 *
 * Usada tanto na tela de Documentos do Cliente quanto no modal de Nova Ordem de Serviço,
 * garantindo que todo arquivo anexado utilize o mesmo método (bucket + tabela).
 *
 * - Storage: bucket `documentos-clientes`, caminho `${clienteId}/${tipo}_${timestamp}.${ext}`
 * - Metadados: tabela `documentos_clientes`
 */

export const BUCKET_DOCUMENTOS_CLIENTES = 'documentos-clientes'

export const TIPOS_DOCUMENTO_CLIENTE = [
  { value: 'rg_cnh', label: 'RG / CNH' },
  { value: 'comprovante_residencia', label: 'Comprovante de Residência' },
  { value: 'procuracao_equatorial', label: 'Procuração Equatorial' },
  { value: 'contrato_assinado', label: 'Contrato Assinado' },
  { value: 'nota_fiscal', label: 'Nota Fiscal' },
  { value: 'art_trt', label: 'ART/TRT' },
  { value: 'unifilar', label: 'Unifilar' },
  { value: 'lista_material', label: 'Lista de Material' },
  { value: 'analise_sombreamento', label: 'Análise de Sombreamento' },
  { value: 'vistoria', label: 'Vistoria' }
]

const MAX_TAMANHO_BYTES = 20 * 1024 * 1024 // 20MB
const TIPOS_MIME_TODOS = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const TIPOS_MIME_PDF = ['application/pdf']

/** Retorna o rótulo amigável de um tipo de documento. */
export function getTipoDocumentoLabel(tipo) {
  const doc = TIPOS_DOCUMENTO_CLIENTE.find(d => d.value === tipo)
  return doc ? doc.label : tipo
}

/**
 * Valida um arquivo (tamanho e tipo). Retorna { ok, error }.
 */
export function validarArquivoDocumento(file, { apenasPdf = false } = {}) {
  if (!file) return { ok: false, error: 'Nenhum arquivo selecionado.' }
  if (file.size > MAX_TAMANHO_BYTES) {
    return { ok: false, error: 'O arquivo selecionado excede o tamanho máximo permitido de 20MB.' }
  }
  const permitidos = apenasPdf ? TIPOS_MIME_PDF : TIPOS_MIME_TODOS
  if (!permitidos.includes(file.type)) {
    return {
      ok: false,
      error: apenasPdf
        ? 'Por favor, selecione um arquivo PDF.'
        : 'Por favor, selecione um arquivo nos formatos: PDF, JPG, PNG ou WebP.'
    }
  }
  return { ok: true }
}

/**
 * Faz upload de um documento para o Storage e registra os metadados na tabela.
 * Retorna { publicUrl }.
 */
export async function uploadDocumentoCliente({
  clienteId,
  tipoDocumento,
  arquivo,
  observacao = null,
  enviadoPor = 'Sistema'
}) {
  if (!clienteId) throw new Error('Cliente não informado para anexar o documento.')
  if (!arquivo) throw new Error('Nenhum arquivo selecionado.')

  const fileExt = arquivo.name.split('.').pop()
  const fileName = `${clienteId}/${tipoDocumento}_${Date.now()}.${fileExt}`

  const { error: uploadError } = await supabase.storage
    .from(BUCKET_DOCUMENTOS_CLIENTES)
    .upload(fileName, arquivo, { cacheControl: '3600', upsert: false })

  if (uploadError) {
    if (uploadError.message?.includes('Bucket not found')) {
      throw new Error('❌ Bucket não encontrado! Você precisa criar o bucket "documentos-clientes" no Supabase Storage primeiro. Veja o arquivo docs/CRIAR_BUCKET_PASSO_A_PASSO.md')
    }
    throw uploadError
  }

  const { data: { publicUrl } } = supabase.storage
    .from(BUCKET_DOCUMENTOS_CLIENTES)
    .getPublicUrl(fileName)

  const { error: dbError } = await supabase
    .from('documentos_clientes')
    .insert([{
      cliente_id: clienteId,
      tipo_documento: tipoDocumento,
      nome_arquivo: arquivo.name,
      caminho_arquivo: publicUrl,
      observacao: observacao || null,
      enviado_por: enviadoPor
    }])

  if (dbError) throw dbError

  return { publicUrl }
}
